import { css } from '@emotion/react/macro';
import styled from '@emotion/styled/macro';
import { DataTable } from '../../data-table';
import { TitleCard } from '../../card';
import { styledTitleCardSectionTitle } from '../claim-section.styles';
import { BaseButton, Variables } from '../../base';
import { Icon } from '@mdi/react';
import MarkdownWrapper from '@enter/core/src/ui/base/markdown-wrapper/markdown-wrapper';
import { ITimelineEventStatusTagTone } from './claim-timeline.types';

export function statusTagToneColors(tone: ITimelineEventStatusTagTone): { bg: string; fg: string; dot: string } {
    switch (tone) {
        case 'pending':
            return {
                bg: Variables.ThemeLight.AttentionMild1,
                fg: Variables.ThemeLight.AttentionConstant1,
                dot: Variables.ThemeLight.AttentionConstant6,
            };
        case 'enter':
            return {
                bg: Variables.ThemeLight.PrimaryMild1,
                fg: Variables.ThemeLight.PrimaryConstant1,
                dot: Variables.ThemeLight.PrimaryConstant6,
            };
        case 'success':
            return {
                bg: Variables.ThemeLight.SuccessMild1,
                fg: Variables.ThemeLight.SuccessConstant1,
                dot: Variables.ThemeLight.SuccessConstant6,
            };
        case 'attention':
            return {
                bg: Variables.ThemeLight.WarningMild1,
                fg: Variables.ThemeLight.WarningConstant1,
                dot: Variables.ThemeLight.WarningConstant6,
            };
        case 'accent':
            return {
                bg: Variables.Colors.rose5,
                fg: Variables.Colors.rose20,
                dot: Variables.Colors.rose20,
            };
        case 'neutral':
        default:
            return {
                bg: Variables.ThemeLight.NeutralMild1,
                fg: Variables.ThemeLight.TextMedium,
                dot: Variables.ThemeLight.TextMild,
            };
    }
}

/**
 * Consolidated styles for ClaimTimeline component
 * Merged from Provider's claim-timeline-table.styles.tsx and Support's timeline.styles.ts
 */

// ============================================================================
// Common Interfaces
// ============================================================================

interface ISelectableProps {
    selectable: boolean;
}

interface IAlign {
    align?: 'left' | 'right';
}

interface IExpandParent {
    expand?: boolean;
}

// ============================================================================
// Common Styles
// ============================================================================
const hoverable = css`
    &:hover {
        background-color: ${Variables.Colors.n5};
    }
`;

const selectableStyle = (props: ISelectableProps) => css`
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: ${props.selectable ? 'pointer' : 'default'};
`;

// ============================================================================
// Wrapper Components (Provider uses TitleCard, Support uses TableWrapper)
// ============================================================================

export const StyledTitleCard = styled(TitleCard)`
    ${styledTitleCardSectionTitle}
    border-radius: 12px;
    background-color: ${Variables.Swatches.Gray1};
    & > section > section:first-of-type {
        display: none;
    }
`;

export const TableWrapper = styled.section`
    border-radius: 3px;
    box-shadow: ${Variables.Shadows.small}, ${Variables.Shadows.border};
    background-color: ${Variables.Colors.white};

    div[role='row'] {
        [role='cell']:first-of-type {
            padding: 0;
        }
    }
`;

export const PaymentsToggleRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 0 20px 0 0;
`;

// ============================================================================
// Data Table Styles
// ============================================================================

export const StyledDataTable = styled(DataTable)`
    display: flex;
    flex-direction: column;
    min-height: 200px;
    margin: 0 14px;

    & > div {
        overflow-y: hidden;
        box-shadow: none;
    }

    /* Override DataTable's internal table overflow */
    div[role='table'] {
        overflow-x: visible;
        overflow-y: visible;

        & > div:first-of-type {
            [role='columnheader'] {
                white-space: nowrap;
            }
        }
    }

    div[role='row'] {
        ${hoverable}
        height: auto;
    }

    div[role='columnheader'] {
        padding: 16px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 12px;
        font-weight: 600;
        color: ${Variables.Colors.n50};
        border-right: none;

        &:first-of-type {
            padding: 0;
        }
    }

    div[role='cell'] {
        color: ${Variables.ThemeLight.TextMedium};
        font-size: 14px;
    }

    div[role='row'] {
        [role='cell']:first-of-type {
            padding: 0;
            align-items: stretch;
        }
    }
`;

// ============================================================================
// Cell Components
// ============================================================================

export const SelectableCell = styled.span<ISelectableProps>`
    ${selectableStyle};
    display: block;
`;

export const StyledSegment = styled(SelectableCell)`
    ${selectableStyle};
    padding: 0;
    display: flex;
    align-items: stretch;
    justify-content: center;
    width: 100%;
    height: 100%;
    align-self: stretch;
`;

export const StyledDescription = styled(SelectableCell)`
    color: ${Variables.Colors.n100};
    width: 100%;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    font-size: 14px;

    & * {
        font-size: 14px !important;
        letter-spacing: normal;
    }
`;

export const Description = styled.span`
    display: flex;
    align-items: center;
    width: 100%;
    font-size: 14px;
`;

export const DescriptionItem = styled.span`
    &:not(:first-of-type) {
        margin-left: 8px;
    }
`;

// ============================================================================
// Event Display Components
// ============================================================================

export const StyledEvent = styled.span`
    color: ${Variables.Colors.n100};
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.platinum};
    border-radius: ${Variables.Rounded.medium};
    padding: 2px 6px;

    &:not(:first-of-type) {
        margin-left: 4px;
    }
`;

// Support's Event component (with icon)
export const Event = styled.div`
    display: flex;
    align-items: center;
    font-size: 14px;
    color: ${Variables.Colors.n100};
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.platinum};
    border-radius: ${Variables.Rounded.medium};
    padding: 2px 6px;
`;

export const EventLabel = styled.div``;

export const SecondaryEvent = styled.div`
    display: flex;
    align-items: center;
    margin-left: 5px;
`;

// ============================================================================
// Icon Components
// ============================================================================

export const StyledIcon = styled(Icon)`
    margin-top: 4px;
    height: 16px;
    width: 16px;
    fill: ${Variables.Colors.b300};
    align-self: center;
`;

export const EventIcon = styled(Icon)`
    fill: ${Variables.Colors.n70};
    text-align: center;
    margin-right: 5px;
`;

export const ExpandIcon = styled(Icon)`
    fill: ${Variables.Colors.b300};
`;

export const AttachIcon = styled(Icon)`
    transform: rotate(45deg);
    margin-right: 4px;
    fill: ${Variables.Colors.n30};
`;

export const DocumentIcon = styled(Icon)`
    height: 64px;
    width: 64px;
    fill: ${Variables.Colors.n50};
    margin-right: 32px;
    cursor: pointer;
`;

// ============================================================================
// Expandable Row Components
// ============================================================================

export const ExpandRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    padding: 0;
    width: 100%;
`;

export const ExpandSegment = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 50px;
`;

export const SegmentContainer = styled.section`
    min-width: 50px;
`;

export const SubRowContainer = styled.section`
    display: flex;
    width: 100%;
`;

export const ExpandCell = styled.div`
    display: flex;
    flex-grow: 1;
    padding: 16px 0px;
    width: 100%;
    min-width: 0;
`;

export const ExpandCol = styled.div`
    width: 100%;
`;

export const ExpandWrap = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    flex-grow: 1;
    width: 100%;
    min-width: 0;
`;

export const ExpandContainer = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
`;

// ============================================================================
// Expand Table Components (Provider-specific detail tables)
// ============================================================================

export const ExpandTable = styled.table<IExpandParent>`
    white-space: normal;
    color: ${Variables.Colors.n100};

    ${({ expand }) =>
        expand
            ? css`
                  margin: 0;
              `
            : css`
                  min-width: 40%;
              `}
`;

export const ExpandTD = styled.td<IAlign>`
    text-align: ${(props: IAlign): string => (props.align ? props.align : 'left')};
`;

export const ExpandTDCommentBody = styled(ExpandTD)`
    overflow: visible;
    text-overflow: unset;
    white-space: normal;
`;

export const ExpandTH = styled.th<IAlign>`
    text-align: ${(props: IAlign): string => (props.align ? props.align : 'left')};
    vertical-align: top;
    font-weight: 500;
`;

export const ExpandTR = styled.tr`
    & ${ExpandTD}, & ${ExpandTH} {
        font-size: 16px;
        font-weight: 400;
        letter-spacing: 0.5px;
        padding: 4px 8px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export const ExpandTHead = styled.thead`
    ${ExpandTH} {
        font-size: 20px;
        padding-bottom: 16px;
    }
`;

export const ExpandTBody = styled.tbody``;

export const ExpandTFoot = styled.tfoot``;

export const ExpandSubTH = styled(ExpandTH)``;

export const ExpandTitleTH = styled(ExpandTH)``;

export const ExpandTitle = styled.h3`
    margin: 18px 0 0 0;
    font-weight: 500;
    font-size: 16px;
    text-decoration: underline;
`;

export const ExpandListContainer = styled.div`
    width: 100%;
    margin-top: 16px;
`;

export const ExpandListItem = styled.div`
    margin-bottom: 16px;
    &:last-of-type {
        margin-bottom: 0;
    }
`;

export const ExpandSubtitle = styled.div`
    font-weight: 500;
    margin-bottom: 4px;
`;

export const ExpandLabel = styled.div`
    font-weight: 500;
    padding: 5px 8px;
`;

// ============================================================================
// Utility Components
// ============================================================================

export const StyledLink = styled.a`
    color: ${Variables.Colors.p300};
    text-decoration: underline;
`;

export const Attachment = styled.span`
    display: inline-flex;
    align-items: center;
`;

export const MarkdownBlock = styled(MarkdownWrapper)`
    margin: 0;
`;

export const ClaimBundleWrapper = styled.div`
    display: flex;
    padding-left: 16px;
    margin: 10px;
`;

export const Divider = styled.div`
    width: 100%;
    height: 1px;
    background: ${Variables.Colors.platinum};
    margin: 16px 0;
    position: relative;
`;

export const FilterSelectTrigger = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border: 1px solid ${Variables.ThemeLight.Border2};
    background: ${Variables.ThemeLight.Background3};
    border-radius: 6px;
    font-family: ${Variables.Fonts.calibre};
    font-size: ${Variables.MetricsValue.TypographySm}px;
    font-weight: ${Variables.FontWeight.medium};
    line-height: 1;
    color: ${Variables.ThemeLight.TextMedium};
    cursor: pointer;
    box-sizing: border-box;
`;
export const FilterSelectContainer = styled.div`
    margin: 14px 24px 8px;
`;

export const RowGroupWrapper = styled.div`
    text-align: center;
    background: ${Variables.Colors.n15_48};
    padding: 10px 0;
    cursor: pointer;
    user-select: none;
    border-top: 1px solid ${Variables.Colors.n15};
    border-bottom: 1px solid ${Variables.Colors.n15};

    &:hover {
        background: ${Variables.Colors.n15_16};
    }
`;

export const RowWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

export const Wrapper = styled.div`
    width: 100%;
`;

export const CommentBodyContainer = styled.div`
    max-width: 100%;
`;

// ============================================================================
// Detail Components (Provider-specific)
// ============================================================================

export const DetailBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 16px;
`;

export const DetailLabel = styled.div`
    font-weight: 500;
    color: ${Variables.Colors.n100};
`;

export const DetailValue = styled.div`
    color: ${Variables.Colors.n100};
    word-break: break-word;
`;

export const ExpandDetailButton = styled(BaseButton)`
    margin: 10px 0;
    padding: 6px 12px;
`;

export const ExpandIframe = styled.iframe`
    padding: 10px 15px;
    width: 100%;
    min-height: 200px;
    overflow-x: auto;
    border: 1px solid ${Variables.Colors.platinum};
    border-radius: ${Variables.Rounded.small};
`;

export const PayloadWrapper = styled.div`
    width: 100%;
`;

export const JsonViewersContainer = styled.div`
    display: flex;
    width: 100%;
    align-items: flex-start;
    overflow: hidden;
`;

export const JsonViewerColumn = styled.div`
    flex: 1;
    min-width: 0;
    max-width: 33.33%;
`;

export const JsonViewerLabel = styled.div`
    font-weight: 500;
    margin-bottom: 8px;
`;

export const JsonViewerWrapper = styled.div`
    & > div {
        font-size: 14px;
    }
`;

export const JsonViewToolbar = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
`;

// ============================================================================
// Re-exported from shared timeline styles
// ============================================================================

export const TablesGroup = styled.div`
    width: 100%;
    display: flex;
    > div {
        width: 50%;
        &:not(:first-of-type) {
            margin-left: 40px;
        }
    }
`;

export const ScrollableCodeContainer = styled.div`
    max-height: none;
    overflow: visible;
    border-radius: ${Variables.Rounded.small};
    padding: 8px;
    background-color: transparent;
    box-sizing: border-box;
    margin: 0 10px;

    textarea {
        overflow: hidden !important;
        max-height: none !important;
        height: auto !important;
    }
`;
export const SecondaryIcon = styled(Icon)`
    fill: ${Variables.Colors.n70};
    border: 1px solid ${Variables.Colors.platinum};
    border-radius: 6px;
    background: ${Variables.Colors.white};
`;

export const FlexWrapper = styled.div`
    display: flex;
`;

export const Subtitle = styled.div`
    font-size: 16px;
    font-weight: 600;
`;

export const ResourceTable = styled.section`
    width: 100%;
    padding: 10px;
    box-shadow: ${Variables.Shadows.border};
`;

// ============================================================================
// Redesigned timeline list
// ============================================================================

export const RedesignList = styled.div`
    margin: 0 24px 8px;
`;

export const RedesignLoading = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    margin: 0 24px 8px;
`;

export const RedesignHeader = styled.div<{ $columns: string }>`
    display: grid;
    grid-template-columns: ${({ $columns }) => $columns};
    gap: 14px;
    align-items: center;
    padding: 10px 0 10px 36px;
    border-bottom: 1px solid ${Variables.ThemeLight.Border2};
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextMild};
`;

export const RedesignRows = styled.div`
    position: relative;
`;

export const RedesignRow = styled.div<{ $showBorder: boolean }>`
    position: relative;
    border-bottom: ${({ $showBorder }) => ($showBorder ? `1px solid ${Variables.ThemeLight.Border2}` : 'none')};
`;

export const RedesignRowHeader = styled.div<{
    $columns: string;
    $expanded: boolean;
    $expandable: boolean;
}>`
    position: relative;
    display: grid;
    grid-template-columns: ${({ $columns }) => $columns};
    gap: 14px;
    align-items: center;
    padding: 12px 0 12px 36px;
    cursor: ${({ $expandable }) => ($expandable ? 'pointer' : 'default')};
    background: ${({ $expanded }) => ($expanded ? Variables.ThemeLight.PrimaryMild1 : 'transparent')};
    border-radius: ${({ $expanded }) => ($expanded ? '6px 6px 0 0' : '0')};
    transition: background 120ms ease;

    &:hover {
        background: ${({ $expanded, $expandable }) =>
            !$expanded && $expandable ? Variables.Colors.n5 : undefined};
    }
`;

export const RedesignDot = styled.span<{ $isFirst: boolean }>`
    position: absolute;
    left: 0;
    top: 24px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ $isFirst }) => ($isFirst ? Variables.ThemeLight.PrimaryConstant6 : Variables.ThemeLight.Background3)};
    border: 2px solid
        ${({ $isFirst }) => ($isFirst ? Variables.ThemeLight.PrimaryConstant6 : Variables.ThemeLight.TextXMild)};
    box-sizing: border-box;
`;

export const RedesignDate = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    color: ${Variables.ThemeLight.TextMedium};
    font-variant-numeric: tabular-nums;
`;

export const EventChipWrap = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

export const EventChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    max-width: 200px;
    border: 1px solid ${Variables.ThemeLight.Border2};
    background: ${Variables.ThemeLight.Background3};
    border-radius: 8px;
    padding: 6px 10px;
    font-family: ${Variables.Fonts.calibre};
    font-size: 13px;
    font-weight: 500;
    line-height: 1.2;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const EventChipIcon = styled(Icon)`
    flex: none;
    fill: ${Variables.ThemeLight.TextMild};
`;

export const EventChipLabel = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const EventChipBadge = styled.span`
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 6px;
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    font-weight: 500;
    color: ${Variables.ThemeLight.TextMild};
    font-style: italic;
    flex: none;
`;

export const RedesignDescription = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    font-weight: 400;
    color: ${Variables.ThemeLight.TextMedium};
    min-width: 0;

    & * {
        font-size: 14px !important;
        letter-spacing: normal;
    }

    table tbody td[data-amount-value] {
        font-weight: 600;
        color: ${Variables.ThemeLight.TextStrong};
        font-variant-numeric: tabular-nums;
    }
`;

export const RedesignAmount = styled.span`
    font-size: 14px;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const RedesignType = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    color: ${Variables.ThemeLight.TextMedium};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const RedesignTagsCell = styled.span`
    min-width: 0;
`;

export const RedesignActionsCell = styled.span`
    min-width: 0;
`;

export const RedesignExpandIcon = styled(Icon, {
    shouldForwardProp: prop => prop !== '$expanded' && prop !== '$visible',
})<{ $expanded: boolean; $visible: boolean }>`
    visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
    width: 18px;
    height: 18px;
    font-size: 18px;
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 18;
    line-height: 1;
    fill: var(--text-mild, ${Variables.ThemeLight.TextMild});
    color: var(--text-mild, ${Variables.ThemeLight.TextMild});
    transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'none')};
    transition: transform 150ms;
`;

export const RedesignExpandPanel = styled.div`
    padding: 4px 8px 16px 36px;
    background: ${Variables.ThemeLight.Background1};
    border-radius: 0 0 6px 6px;
`;

export const StatusTag = styled.span<{ $bg: string; $fg: string }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 999px;
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    background: ${({ $bg }) => $bg};
    color: ${({ $fg }) => $fg};
    white-space: nowrap;
`;

export const StatusTagDot = styled.span<{ $color: string }>`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    flex: none;
`;

export const EventDetailWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-bottom: 14px;
    padding-right: 28px;
`;

export const EventDetailCard = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px 28px;
    background: ${Variables.ThemeLight.Background1};
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 8px;
    padding: 14px 16px;
`;

export const EventDetailCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
`;

export const EventDetailLabel = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 9px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextXMild};
`;

export const EventDetailValue = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextStrong};
    text-wrap: pretty;
    font-variant-numeric: tabular-nums;
    word-break: break-word;
`;
